import { selectFullAppConfig } from '@src/providers/AppConfigRedux/selectors';
import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  BASE_NAV_PATHS,
  PORTFOLIO_FULL_NAV,
  PORTFOLIO_LITE_NAV,
  type NavigationItem,
} from '@src/tokens/navigationPaths';
import type { ACTION } from '@talos/kyoko';
import { notEmpty } from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag, type FeatureFlags } from './useFeatureFlag';
import { useRoleAuth } from './useRoleAuth';
import { useSpacesNavItems } from './useSpacesNavItems';

/**
 * Filters navigation items by permission and feature flags.
 * @param items - The navigation items to filter.
 * @param isAuthorized - A function that checks if a user is authorized to access a given permission.
 * @param flags - The feature flags to check.
 * @returns The filtered navigation items.
 */
function filterNavigationItemsByPermission(
  items: NavigationItem[],
  isAuthorized: (permission: ACTION) => boolean,
  flags: FeatureFlags
): NavigationItem[] {
  return items
    .filter(item => {
      // Check visibility callback
      if (item.isVisible && !item.isVisible(flags, isAuthorized)) {
        return false;
      }

      return true;
    })
    .map(item => {
      if (!item.children) {
        return item;
      }

      const filteredChildren = filterNavigationItemsByPermission(item.children, isAuthorized, flags);

      // Only include the item if it has visible children after filtering
      if (filteredChildren.length === 0) {
        return null;
      }

      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter(notEmpty);
}

/**
 * A hook to get the navigation items, filtered by permission and feature flags.
 * @returns An object containing all the navigation items, top navigation items, bottom navigation items, and user menu items.
 */
export function useNavigationItems() {
  const { isAuthorized } = useRoleAuth();
  const flags = useFeatureFlag();

  const appConfig = useAppStateSelector(selectFullAppConfig);
  const { enablePortfolioManagementSystem } = useFeatureFlag();
  // Don't rely on PortfolioSettingsContext here, use the appConfig directly
  const enablePMSFull = appConfig.enablePMSLayout && enablePortfolioManagementSystem;

  const tradingNav = useSpacesNavItems('trading');
  const monitoringNav = useSpacesNavItems('monitoring');

  const navItems = useMemo(() => {
    // Select appropriate portfolio navigation based on PMS mode
    const portfolioNav = enablePMSFull ? PORTFOLIO_FULL_NAV : PORTFOLIO_LITE_NAV;

    // Replace dynamic nav item with selected structure
    const navPaths = BASE_NAV_PATHS.map(item => {
      const newItem = { ...item };
      if (item.path === '/portfolio') {
        newItem.children = portfolioNav;
        return newItem;
      }
      if (item.path === '/trading') {
        // Spaces first, then any static children (omit space groups when trading spaces are disabled)
        const spaceChildren = flags.enableTradingSpaces ? (tradingNav ?? []) : [];
        const merged = [...spaceChildren, ...(item.children ?? [])];
        // Avoid `children: []` — filterNavigationItemsByPermission drops items whose children filter to empty
        newItem.children = merged.length > 0 ? merged : undefined;
        if (tradingNav && flags.enableTradingSpaces) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      if (newItem.path === '/monitoring') {
        const spaceChildren = flags.enableMonitoringSpaces ? (monitoringNav ?? []) : [];
        const merged = [...(item.children ?? []), ...spaceChildren];
        newItem.children = merged.length > 0 ? merged : undefined;
        if (flags.enableMonitoringSpaces) {
          newItem.enableSpaces = true;
        }
        return newItem;
      }
      return newItem;
    });

    return filterNavigationItemsByPermission(navPaths, isAuthorized, flags);
  }, [isAuthorized, flags, enablePMSFull, tradingNav, monitoringNav]);

  return useMemo(
    () => ({
      navItems,
      topNavItems: navItems.filter(item => item.position === 'top'),
      bottomNavItems: navItems.filter(item => item.position === 'bottom'),
      userMenuItems: navItems.filter(item => item.position === 'user-menu'),
    }),
    [navItems]
  );
}
